import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  EyeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UserIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

import { useContentValidation } from '../../store/contentAdminStore';
import type { ContentSnapshot } from '../../services/contentAdminService';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  versionId: string;
}

type PreviewTab = 'overview' | 'questions' | 'profiles' | 'matrix' | 'spider';

interface PreviewData {
  questions: any;
  evaluation_matrix: any;
  report_contents: any;
  spider_chart: any;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  versionId,
}) => {
  const [activeTab, setActiveTab] = useState<PreviewTab>('overview');
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { previewContent } = useContentValidation();

  const tabs = [
    { id: 'overview' as const, name: 'Visão Geral', icon: EyeIcon },
    { id: 'questions' as const, name: 'Questões', icon: DocumentTextIcon },
    { id: 'profiles' as const, name: 'Perfis', icon: UserIcon },
    { id: 'matrix' as const, name: 'Matriz', icon: ChartBarIcon },
    { id: 'spider' as const, name: 'Gráfico', icon: ChartBarIcon },
  ];

  useEffect(() => {
    if (isOpen && versionId) {
      loadPreviewData();
    }
  }, [isOpen, versionId]);

  const loadPreviewData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await previewContent(versionId);
      if (data) {
        setPreviewData({
          questions: data.questions,
          evaluation_matrix: data.evaluation_matrix,
          report_contents: data.report_contents,
          spider_chart: data.spider_chart,
        });
      } else {
        setError('Não foi possível carregar os dados para preview');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveTab('overview');
    setPreviewData(null);
    setError(null);
    onClose();
  };

  const OverviewTab = () => {
    if (!previewData) return null;

    const stats = {
      questions: previewData.questions?.questoes?.length || 0,
      dimensions: previewData.questions?.dimensoes?.length || 0,
      profiles: Object.keys(previewData.evaluation_matrix?.perfis || {}).length,
      reportProfiles: Object.keys(previewData.report_contents?.perfis || {}).length,
      spiderAxes: previewData.spider_chart?.eixos?.length || 0,
    };

    const issues = [
      stats.questions === 0 && 'Nenhuma questão configurada',
      stats.dimensions === 0 && 'Nenhuma dimensão configurada',
      stats.profiles === 0 && 'Nenhum perfil na matriz',
      stats.reportProfiles === 0 && 'Nenhum perfil de relatório',
      stats.spiderAxes === 0 && 'Nenhum eixo no gráfico spider',
      stats.profiles !== stats.reportProfiles && 'Inconsistência entre perfis da matriz e relatórios',
    ].filter(Boolean);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.questions}</div>
            <div className="text-sm text-gray-600">Questões</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.dimensions}</div>
            <div className="text-sm text-gray-600">Dimensões</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{stats.profiles}</div>
            <div className="text-sm text-gray-600">Perfis</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{stats.reportProfiles}</div>
            <div className="text-sm text-gray-600">Perfis de Relatório</div>
          </div>
          <div className="bg-teal-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-teal-600">{stats.spiderAxes}</div>
            <div className="text-sm text-gray-600">Eixos Spider</div>
          </div>
        </div>

        {issues.length > 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
              <h4 className="font-medium text-yellow-800">Problemas Detectados</h4>
            </div>
            <ul className="text-sm text-yellow-700 space-y-1">
              {issues.map((issue, index) => (
                <li key={index}>• {issue}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
              <h4 className="font-medium text-green-800">Configuração Válida</h4>
            </div>
            <p className="text-sm text-green-700 mt-1">
              Todos os componentes principais estão configurados adequadamente.
            </p>
          </div>
        )}
      </div>
    );
  };

  const QuestionsTab = () => {
    if (!previewData?.questions) return <div>Nenhum dado de questões disponível</div>;

    const { questions } = previewData;

    return (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Informações Gerais</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Título:</span> {questions.titulo}
            </div>
            <div>
              <span className="text-gray-600">Tempo Estimado:</span> {questions.tempo_estimado}
            </div>
            <div>
              <span className="text-gray-600">Total de Questões:</span> {questions.total_questoes}
            </div>
            <div>
              <span className="text-gray-600">Dimensões:</span> {questions.dimensoes?.length || 0}
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Dimensões</h4>
          <div className="space-y-2">
            {questions.dimensoes?.map((dim: any, index: number) => (
              <div key={dim.id} className="border border-gray-200 rounded p-3">
                <div className="font-medium text-sm">{dim.nome}</div>
                <div className="text-xs text-gray-600 mt-1">{dim.descricao}</div>
                <div className="text-xs text-blue-600 mt-1">
                  {questions.questoes?.filter((q: any) => q.dimensao === dim.id).length || 0} questões
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Questões</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {questions.questoes?.map((question: any, index: number) => (
              <div key={question.id} className="border border-gray-200 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center justify-center">
                    {question.id}
                  </span>
                  <span className="text-xs text-gray-600">
                    {questions.dimensoes?.find((d: any) => d.id === question.dimensao)?.nome}
                  </span>
                </div>
                <div className="text-sm text-gray-900">{question.texto}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const ProfilesTab = () => {
    if (!previewData?.report_contents) return <div>Nenhum dado de perfis disponível</div>;

    const { report_contents } = previewData;
    const profiles = Object.entries(report_contents.perfis || {});

    return (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Informações Gerais</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Versão:</span> {report_contents.versao}
            </div>
            <div>
              <span className="text-gray-600">Total de Perfis:</span> {profiles.length}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {profiles.map(([profileId, profile]: [string, any]) => (
            <div key={profileId} className="border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium text-gray-900 mb-2">{profile.nome}</h5>
              <p className="text-sm text-gray-600 mb-3">{profile.descricao_resumida}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-gray-600">Pontos Fortes:</span>
                  <div className="font-medium">{profile.pontos_fortes?.length || 0}</div>
                </div>
                <div>
                  <span className="text-gray-600">Áreas de Desenvolvimento:</span>
                  <div className="font-medium">{profile.areas_desenvolvimento?.length || 0}</div>
                </div>
                <div>
                  <span className="text-gray-600">Plano 30 dias:</span>
                  <div className="font-medium">{profile.plano_acao?.['30_dias']?.length || 0}</div>
                </div>
                <div>
                  <span className="text-gray-600">Estratégias:</span>
                  <div className="font-medium">
                    {Object.values(profile.estrategias_especificas || {}).flat().length}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const MatrixTab = () => {
    if (!previewData?.evaluation_matrix) return <div>Nenhum dado de matriz disponível</div>;

    const { evaluation_matrix } = previewData;
    const profiles = Object.entries(evaluation_matrix.perfis || {});

    return (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Configuração da Matriz</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Algoritmo:</span> {evaluation_matrix.algoritmo?.tipo}
            </div>
            <div>
              <span className="text-gray-600">Perfis:</span> {profiles.length}
            </div>
            <div>
              <span className="text-gray-600">Dimensões:</span> {evaluation_matrix.dimensoes?.length || 0}
            </div>
            <div>
              <span className="text-gray-600">Escala:</span> {evaluation_matrix.configuracao_calculo?.escala_minima}-{evaluation_matrix.configuracao_calculo?.escala_maxima}
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Pesos por Perfil</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Perfil
                  </th>
                  {evaluation_matrix.dimensoes?.map((dim: any) => (
                    <th key={dim.id} className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      {dim.nome}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {profiles.map(([profileId, profile]: [string, any]) => {
                  const total = Object.values(profile.pesos_dimensoes || {}).reduce((sum: number, peso: any) => sum + peso, 0);
                  return (
                    <tr key={profileId}>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {profile.nome}
                      </td>
                      {evaluation_matrix.dimensoes?.map((dim: any) => (
                        <td key={dim.id} className="px-3 py-2 text-center text-sm text-gray-600">
                          {(profile.pesos_dimensoes?.[dim.id] || 0).toFixed(1)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-center text-sm font-medium text-gray-900">
                        {total.toFixed(1)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const SpiderTab = () => {
    if (!previewData?.spider_chart) return <div>Nenhum dado de gráfico spider disponível</div>;

    const { spider_chart } = previewData;

    return (
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Configuração do Gráfico</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Versão:</span> {spider_chart.versao}
            </div>
            <div>
              <span className="text-gray-600">Eixos:</span> {spider_chart.eixos?.length || 0}
            </div>
            <div>
              <span className="text-gray-600">Largura:</span> {spider_chart.configuracao_visual?.largura}px
            </div>
            <div>
              <span className="text-gray-600">Altura:</span> {spider_chart.configuracao_visual?.altura}px
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Eixos Configurados</h4>
          <div className="space-y-2">
            {spider_chart.eixos?.map((eixo: any, index: number) => (
              <div key={eixo.id} className="border border-gray-200 rounded p-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded border"
                    style={{ backgroundColor: eixo.cor }}
                  ></div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{eixo.nome}</div>
                    <div className="text-xs text-gray-600">
                      {eixo.posicao_graus}° • Vinculado a: {eixo.dimensao_vinculada}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {spider_chart.eixos?.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Preview do Gráfico</h4>
            <div className="bg-gray-50 p-4 rounded-lg flex justify-center">
              <svg width="200" height="200" viewBox="0 0 200 200">
                {/* Grid */}
                {[0.2, 0.4, 0.6, 0.8, 1].map((scale, index) => (
                  <circle
                    key={index}
                    cx="100"
                    cy="100"
                    r={80 * scale}
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="1"
                  />
                ))}
                
                {/* Axes */}
                {spider_chart.eixos.map((eixo: any) => {
                  const angle = (eixo.posicao_graus - 90) * (Math.PI / 180);
                  const x = 100 + Math.cos(angle) * 80;
                  const y = 100 + Math.sin(angle) * 80;
                  
                  return (
                    <g key={eixo.id}>
                      <line
                        x1="100"
                        y1="100"
                        x2={x}
                        y2={y}
                        stroke="#E5E7EB"
                        strokeWidth="1"
                      />
                      <text
                        x={x + Math.cos(angle) * 15}
                        y={y + Math.sin(angle) * 15}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="10"
                        fill={eixo.cor}
                      >
                        {eixo.nome}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab />;
      case 'questions':
        return <QuestionsTab />;
      case 'profiles':
        return <ProfilesTab />;
      case 'matrix':
        return <MatrixTab />;
      case 'spider':
        return <SpiderTab />;
      default:
        return <div>Conteúdo não encontrado</div>;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Preview do Conteúdo
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Visualização dos dados configurados
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={loadPreviewData}
                    disabled={isLoading}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowPathIcon className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                  
                  <button
                    onClick={handleClose}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 px-6">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                        isActive
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.name}
                    </button>
                  );
                })}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {isLoading ? (
                  <div className="text-center py-12">
                    <ArrowPathIcon className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Carregando preview...
                    </h3>
                    <p className="text-gray-600">
                      Preparando os dados para visualização
                    </p>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Erro ao carregar preview
                    </h3>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                      onClick={loadPreviewData}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      Tentar Novamente
                    </button>
                  </div>
                ) : (
                  renderTabContent()
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};